<template>
  <div>
    <BaseHeadline
      :showOnMobile="true"
      showBreadcrumbsOnMobile
      :breadcrumbs="breadcrumbs"
      icon="mdi-order-bool-ascending-variant"
      :headerName="headerName"
    >
      <template v-slot:headlineBtns>
        <span v-if="$vuetify.breakpoint.mdAndUp">
          <v-btn
            v-if="!isCompleted && isAssignedTo"
            class="mb-0 mr-2"
            color="success"
            depressed
            @click="save()"
          >
            <v-icon left>mdi-file-download-outline</v-icon>
            Lagre
          </v-btn>
          <v-btn
            v-if="!isCompleted && isAssignedTo"
            class="mb-0 mr-2"
            color="primary"
            depressed
            @click="showChangeResponsiblyDialog = true"
          >
            <v-icon left>mdi-account-outline</v-icon>
            Endre ansvarlig
          </v-btn>
          <v-btn color="primary" depressed @click="downloadChecklistPdf">
            <v-icon left>mdi-file-download-outline</v-icon>
            Last ned fil
          </v-btn>
        </span>
      </template>
    </BaseHeadline>

    <v-btn
      v-if="$vuetify.breakpoint.smAndDown && !isCompleted && isAssignedTo"
      class="mb-4 stickyBtn"
      color="success"
      block
      depressed
      @click="save()"
    >
      Lagre
    </v-btn>

    <ButtonGroup
      class="my-3"
      v-if="$vuetify.breakpoint.smAndDown"
      v-model="buttons"
      @changeResponsibly="showChangeResponsiblyDialog = true"
      @download="downloadChecklistPdf"
    />

    <BaseView v-model="list" class="mt-2">
      <template v-slot="{ element }">
        <ChecklistInfoCard
          class="mb-8"
          v-if="element.type === 'ChecklistInfoCard'"
          :data="checkList"
        />

        <ChecklistTimeline
          v-if="element.type === 'ChecklistTimeline'"
          v-model="checkList"
          :isCompleted="isCompleted"
          :isAssignedTo="isAssignedTo"
          @refresh="refresh"
        />
        <div v-if="checkList && element.type === 'Signature'">
          <div v-if="checkList.signature" class="mb-4">
            <span
              v-for="(signature, index) in checkList.signatures"
              :key="index"
            >
              <v-chip
                class="pa-4 ma-1 elevation-2"
                color="cardSurface"
                :close="false"
                @click.stop="clickOnSignature(signature)"
              >
                <v-avatar left>
                  <v-icon>mdi-file-sign</v-icon>
                </v-avatar>
                {{ signature.fullName }} -
                {{ signature.updated | moment("DD.MM.YYYY HH:mm") }}
              </v-chip>
            </span>
          </div>
          <v-btn
            v-if="checkList.signature && !isCompleted"
            class="mb-6"
            color="secondary"
            depressed
            @click="addSignaturDialog = true"
          >
            Legg til Signatur
          </v-btn>
        </div>
        <div v-if="checkList && element.type === 'String'" class="mb-10">
          <div v-if="checkList.updatedString">
            Oppdatert av {{ checkList.updatedString }}
          </div>
          <div v-if="checkList.completedString">
            Sjekkliste fullført {{ checkList.completedString }}
          </div>
        </div>

        <v-btn
          v-if="
            element.type === 'Completed' &&
            checkList &&
            !isCompleted &&
            !$vuetify.breakpoint.mdAndDown &&
            isAssignedTo
          "
          class="mb-4"
          :block="$vuetify.breakpoint.mdAndDown"
          color="primary"
          @click="showConfirmComplete = true"
        >
          Fullfør sjekkliste
        </v-btn>
      </template>
    </BaseView>

    <v-fab-transition>
      <v-btn
        v-if="$vuetify.breakpoint.mdAndDown && !isCompleted && isAssignedTo"
        class="btnBottomCenter"
        color="primary"
        fixed
        @click="showConfirmComplete = true"
      >
        <!-- <v-icon left>mdi-swap-vertical</v-icon> -->
        Fullfør sjekkliste
      </v-btn>
    </v-fab-transition>

    <div v-if="checkList">
      <ShowSignatureDialog v-model="showSignaturDialog" :data="signatureData" />

      <SignaturDialog v-model="addSignaturDialog" @save="addSignatur" />
      <ChangeResponsiblyDialog
        v-model="showChangeResponsiblyDialog"
        :responsible="responsibleContactPerson"
        :items="contactPersons"
        @save="saveNewResponsibly"
      />

      <ConfirmDialog
        v-model="showConfirmComplete"
        title="Bekreft"
        icon="mdi-clipboard-check-outline"
        text="Sikker på at du vil fullføre sjekklisten? Du kan ikke endre sjekklisten når den først er fullført"
        btnActionName="Bekreft"
        @action="completeChecklist"
      />

      <ConfirmDialog
        v-model="showAttachmentsDialog"
        title="Last ned fil"
        subtitle="Inkludere vedlegg"
        icon="mdi-file-download-outline"
        text="Inkludere bilder fra sjekklistepunkt(er) med type 'Fil'?"
        btnActionName="Ja"
        btnCloseName="Nei"
        @action="downloadPdf(true)"
        @close="downloadPdf(false)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import checklistApi from "@/api/checkListApi";
import { saveFile } from "@/helpers/files";

import { buttonObject } from "@/types/interface";
import { ChecklistDto } from "@/types/checklistInterface";
import BaseHeadline from "@/components/shared/BaseHeadline.vue";
import BaseView from "@/components/shared/BaseView.vue";
import ChecklistInfoCard from "@/components/checklists/checklist/children/ChecklistInfoCard.vue";
import ButtonGroup from "@/components/shared/buttons/ButtonGroup.vue";
import ChecklistTimeline from "@/components/checklists/checklist/children/ChecklistTimeline.vue";
import SignaturDialog from "@/components/shared/dialog/SignaturDialog.vue";
import ChangeResponsiblyDialog from "@/components/shared/dialog/ChangeResponsiblyDialog.vue";
import ShowSignatureDialog from "@/components/shared/dialog/ShowSignatureDialog.vue";
import ConfirmDialog from "@/components/shared/dialog/ConfirmDialog.vue";
import notification from "@/helpers/notification";

export default Vue.extend({
  data() {
    return {
      loading: false,
      showSignaturDialog: false,
      showAttachmentsDialog: false,
      signatureData: null,
      addSignaturDialog: false,
      showConfirmComplete: false,
      showChangeResponsiblyDialog: false,
      checkList: null as null | ChecklistDto,
      breadcrumbs: [],
      contactPersons: [],
      list: {
        lg: [
          [{ type: "ChecklistInfoCard" }],
          [
            { type: "ChecklistTimeline" },
            { type: "Signature" },
            { type: "String" },
            { type: "Completed" },
          ],
        ],
        md: [
          [{ type: "ChecklistInfoCard" }],
          [
            { type: "ChecklistTimeline" },
            { type: "Signature" },
            { type: "String" },
            { type: "Completed" },
          ],
        ],
        sm: [
          [
            { type: "ChecklistInfoCard" },
            { type: "ChecklistTimeline" },
            { type: "Signature" },
            { type: "String" },
          ],
        ],
      },
    };
  },

  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      var data = {
        checklistId: this.$route.params.id,
        // contextId: "555548",
        // parentCheckList: "1454",
      };
      await checklistApi.getChecklist(data).then((x: any) => {
        this.checkList = x.checklist as ChecklistDto;
        this.breadcrumbs = x.pathBt;
        this.contactPersons = x.contactPersons;
        this.loading = false;
        let itemWithAberration = (this as any).checkList.items.find(
          (x) => x.aberration && x.aberration.aberrationId
        );

        if (x.itemActions) {
          this.checkList.items.forEach((i) => {
            i.actionsInfo = "";
            if (itemWithAberration) {
              i.actionsInfo += `Opprett ny tiltak.\r\n Avvik #${itemWithAberration.aberration.aberrationId} er allerede opprettet.`;
            } else {
              i.actionsInfo += "Opprett ny tiltak";
            }

            var info = x.itemActions
              .filter((a) => a.key === i.id)
              .map((x) => x.value)
              .join("");

            i.info = info;
            i.actionsInfo +=
              info && info !== ""
                ? `\r\nTiltak som er opprettet allerede: ${info}.`
                : "";
          });
        }
      });
    },
    async save(goBack = false) {
      await checklistApi.saveChecklist(this.checkList).then(async () => {
        await this.fetchData();
        if (goBack) {
          this.$router.push({ name: "Checklists" });
        }
      });
    },
    saveNewResponsibly(newId) {
      if (this.checkList?.task?.assignedTo === newId) {
        notification.warn("Bruker er allerede ansvarlig!");
      } else if (this.checkList?.task) {
        this.checkList.task.assignedTo = newId;
        this.save(true);
        this.showChangeResponsiblyDialog = false;
      }
    },
    clickOnSignature(signature: any) {
      this.signatureData = signature;
      this.showSignaturDialog = true;
    },
    addSignatur(signature: any) {
      this.checkList?.signatures.push(signature);
      this.addSignaturDialog = false;
    },
    async downloadChecklistPdf() {
      this.showAttachmentsDialog = true;
    },
    async downloadPdf(includeAttachments: boolean) {
      if (includeAttachments) {
        this.showAttachmentsDialog = false;
      }

      if (this.checkList) {
        await checklistApi
          .downloadChecklistPdf(this.checkList?.id || 0, [], includeAttachments)
          .then(saveFile);
      }
    },
    completeChecklist() {
      if (this.checkList) {
        this.checkList.completedDate = new Date();
        this.showConfirmComplete = false;
        this.save(true);
      }
    },
    async refresh() {
      await this.save();
      await this.fetchData();
    },
  },
  computed: {
    ...mapGetters({
      profile: "shared/profile",
    }),
    headerName(): string {
      return this.checkList ? this.checkList.name : "";
    },
    isCompleted(): boolean {
      return this.checkList ? !!this.checkList?.completedDate : true;
    },
    responsibleContactPerson(): string {
      return this.checkList?.task?.assignedToContactPersonPersonFullName || "";
    },
    isAssignedTo(): boolean {
      return this.checkList?.task?.assignedTo === this.profile.id;
    },
    buttons(): Array<buttonObject> {
      var buttons = [
        {
          show: !this.isCompleted && this.isAssignedTo,
          name: "Endre ansvarlig",
          color: "primary",
          emit: "changeResponsibly",
          icon: "mdi-account-outline",
        },
        {
          show: true,
          name: "Last ned fil",
          color: "primary",
          emit: "download",
          badgeColor: "yellow",
          icon: "mdi-file-download-outline",
        },
      ] as Array<buttonObject>;

      return buttons;
    },
  },
  components: {
    BaseHeadline,
    BaseView,
    ChecklistInfoCard,
    ButtonGroup,
    ChecklistTimeline,
    SignaturDialog,
    ChangeResponsiblyDialog,
    ShowSignatureDialog,
    ConfirmDialog,
  },
});
</script>

<style lang="scss" scoped>
.btnBottomCenter {
  z-index: 2;
  left: 50%;
  bottom: calc(50px + env(safe-area-inset-bottom));
  transform: translate(-50%, -50%);
}

.stickyBtn {
  position: -webkit-sticky;
  position: sticky;
  top: 75px;
  z-index: 2;
}
</style>
